import moment from "moment/moment";

const EMPTY_VALUE = "—";

const inFormatter = new Intl.NumberFormat("en-IN");
const inDecimalFormatter = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const formatWeightValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return null;

  return inDecimalFormatter.format(parsedValue);
};

const formatMinutes = (minutes) => {
  const parsedMinutes = Number(minutes);
  if (!Number.isFinite(parsedMinutes)) return EMPTY_VALUE;

  const normalizedMinutes = ((parsedMinutes % 1440) + 1440) % 1440;
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = Math.floor(normalizedMinutes % 60);
  const hour12 = hours % 12 || 12;
  const minuteLabel = mins ? `:${String(mins).padStart(2, "0")}` : "";
  const meridiem = hours < 12 ? "am" : "pm";

  return `${hour12}${minuteLabel}${meridiem}`;
};

export const formatSlotDate = (value) => {
  if (!value) return EMPTY_VALUE;

  const date = moment(value);
  if (!date.isValid()) return EMPTY_VALUE;

  const today = moment().startOf("day");
  const tomorrow = moment().add(1, "day").startOf("day");
  const dateStart = date.clone().startOf("day");

  const dayLabel = dateStart.isSame(today)
    ? "Today"
    : dateStart.isSame(tomorrow)
    ? "Tomorrow"
    : date.format("dddd"); // Monday, Tuesday, Wednesday …

  return `${date.format("D MMM")}, ${dayLabel}`;
};

export const formatScheduleDate = (value) => {
  if (!value) return EMPTY_VALUE;

  const date = moment(value);
  if (!date.isValid()) return EMPTY_VALUE;

  return `${date.format("D MMM")} ’${date.format("YY")}`;
};

export const formatSKUCount = (value) => {
  const count = Number(value);
  const normalizedCount = Number.isFinite(count) ? count : 0;
  return `${inFormatter.format(normalizedCount)} ${normalizedCount === 1 ? "SKU" : "SKUs"}`;
};

export const formatPurchaseOrderCount = (value) => {
  const count = Number(value);
  const normalizedCount = Number.isFinite(count) ? count : 0;

  return `${inFormatter.format(normalizedCount)} ${normalizedCount === 1 ? "PO" : "POs"}`;
};

export const formatWeight = (value) => {
  const formattedValue = formatWeightValue(value);
  return formattedValue === null ? EMPTY_VALUE : `${formattedValue} kg`;
};

export const formatInvoiceAmount = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return EMPTY_VALUE;

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(parsedValue);
};

export const formatSlotDisplayName = (slot = {}) => {
  if (
    Number.isFinite(Number(slot.start_mins)) &&
    Number.isFinite(Number(slot.end_mins))
  ) {
    return `${formatMinutes(slot.start_mins)} - ${formatMinutes(slot.end_mins)}`;
  }

  if (typeof slot.display_name === "string" && slot.display_name.trim()) {
    return slot.display_name.trim();
  }

  return EMPTY_VALUE;
};

export const formatStatus = (value) => {
  if (typeof value !== "string" || !value.trim()) return "Scheduled";

  return value
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

export const formatScheduleGroupNumber = (scheduleGroup = {}) =>
  scheduleGroup.schedule_group_number ||
  (scheduleGroup.schedule_group_id
    ? `SC-${scheduleGroup.schedule_group_id}`
    : EMPTY_VALUE);

export const hasShippingNote = (invoice = {}, currentAsnId = 0) => {
  const asnId = Number(invoice.asn_id);
  return (
    Number.isInteger(asnId) && asnId > 0 && asnId !== Number(currentAsnId)
  );
};

export const partitionInvoicesByShippingNote = (
  invoices = [],
  currentAsnId = 0
) => {
  const normalizedInvoices = Array.isArray(invoices) ? invoices : [];

  return normalizedInvoices.reduce(
    (result, invoice) => {
      if (hasShippingNote(invoice, currentAsnId)) {
        result.dispatchedInvoices.push(invoice);
      } else {
        result.availableInvoices.push(invoice);
      }
      return result;
    },
    { availableInvoices: [], dispatchedInvoices: [] }
  );
};

export const formatShippingNoteNumber = (invoice = {}) => {
  if (typeof invoice.asn_number === "string" && invoice.asn_number.trim()) {
    return invoice.asn_number.trim();
  }

  return hasShippingNote(invoice) ? `ASN-${invoice.asn_id}` : EMPTY_VALUE;
};

export const DEFAULT_MIN_T_PLUS_EDD = 10;

export const canSchedulePO = (eddDate, minDays = DEFAULT_MIN_T_PLUS_EDD) => {
  if (!eddDate) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const edd = new Date(eddDate);
  edd.setHours(0, 0, 0, 0);
  const daysUntilEDD = (edd - today) / (1000 * 60 * 60 * 24);
  return daysUntilEDD <= minDays;
};

export const getSchedulingEnabledDate = (eddDate, minDays = DEFAULT_MIN_T_PLUS_EDD) => {
  if (!eddDate) return null;
  const edd = new Date(eddDate);
  const enableDate = new Date(edd);
  enableDate.setDate(enableDate.getDate() - minDays);
  return enableDate.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
};

export const getMinDaysFromConfig = (asnConfig) => {
  const raw = Number(asnConfig?.min_t_plus_edd_for_schedule_creation);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_MIN_T_PLUS_EDD;
};

/** "WH-GGN2, Gurgaon" -> { code: "WH-GGN2", city: "Gurgaon" } */
export const splitWarehouseName = (raw = "") => {
  if (!raw) return { code: "", city: "" };
  const commaIndex = raw.indexOf(",");
  if (commaIndex === -1) return { code: raw, city: "" };
  return { code: raw.slice(0, commaIndex).trim(), city: raw.slice(commaIndex + 1).trim() };
};
